<template>
    <div class="group/entry py-1 px-2"
        :class="[
            index > 0 ? 'border-t border-gray-300' : '',
            has_templates && index === count - 1 ? 'border-b mb-2' : '',
            show_dropdown ? 'bg-gray-100' : 'hover:bg-blue-50',
        ]"
        @click.stop="handleRowClick"
        @click.right.prevent.stop="handleRowClick"
    >
        <div class="flex flex-col md:flex-row items-start md:items-center justify-between">
            <div v-if="! is_editing"
                class="flex flex-1 items-center leading-snug pr-6 pt-1 pb-0.5"
                :class="[
                    show_dropdown ? 'bg-transparent' : '',
                ]"
            >
                <!-- Selection checkbox -->
                <div v-if="selection_mode" class="flex items-center mr-3 mb-0.5">
                    <input
                        type="checkbox"
                        :checked="is_selected"
                        @click.stop="handleCheckboxClick"
                        @click.right.prevent.stop="handleCheckboxClick"
                        class="w-4 h-4"
                    />
                </div>

                <span class="flex items-start mr-4 group-hover/entry:text-blue-600"
                    :class="show_dropdown ? 'text-blue-600' : 'text-black group-hover/entry:text-blue-600'"
                >
                    <file-icon v-if="is_editable" size="14" class="inline mr-2 mt-0.5" />

                    <div class="font-semibold text-base leading-snug select-none">
                        {{ display_filename }} <span class="font-normal text-gray-600 text-xs ml-3 mt-1">{{ document.size_for_humans }}</span>
                    </div>
                </span>
            </div>

            <div v-else
                class="flex flex-1 items-center leading-snug pr-6"
            >
                <span class="flex-1 flex items-start mr-4 group-hover/entry:text-blue-600">
                    <file-icon size="14" class="inline text-green-warm-600 mr-2 mt-1.5" />

                    <input class="flex-1 text-black text-base mr-4 p-0.5 border border-gray-400"
                        ref="input"
                        v-model="new_filename"
                        spellcheck="false"
                        @keydown.enter.prevent="saveFilename"
                        @keydown.escape.prevent="cancelEditFilename"
                    />
                </span>
            </div>

            <div class="flex items-center text-gray-600 group-hover/entry:text-blue-500">
                <div v-if="document.docusign_document" class="mr-2">
                    <i class="fa fa-pen-nib"></i>
                </div>

                <div v-if="!disable_contextual_menus" class="hidden group-hover/entry:flex items-center justify-center w-6 h-6 rounded-full">
                    <more-horizontal-icon size="16" />
                </div>
            </div>
        </div>

        <div v-if="is_editing" class="w-full md:w-auto flex pt-1">
            <div class="flex flex-1 items-center justify-start h-12 space-x-4 pt-2 pl-3">
                <div class="text-xs text-gray-600 hover:text-gray-700"
                    @click.stop="cancelEditFilename"
                >
                    <i class="far fa-times-circle mr-1"></i>
                    Cancel
                </div>

                <div class="flex items-center h-8 text-xs text-green-warm-600 hover:text-green-warm-800 px-3 rounded border border-green-warm-600 hover:border-green-warm-700 hover:bg-green-warm-100"
                    @click.stop="saveFilename"
                >
                    <i class="far fa-check-circle mr-1 pb-0.5"></i>
                    Save Changes
                </div>
            </div>
        </div>

        <contextual-dropdown
            v-if="!disable_contextual_menus"
            :show="show_dropdown"
            :items="dropdown_items"
            :x="dropdownX"
            :y="dropdownY"
            @item-clicked="handleDropdownAction"
            @close="closeDropdown"
        />

        <document-delete-modal
            :show="show_delete_confirmation"
            :document="document"
            :delete_route="document.delete_route"
            :modal_name="`document-delete-modal-${document.id}`"
            @deleted="deletedFile"
            @closed="confirmDeleteClosed"
        />
    </div>
</template>

<script>
import { FileIcon, MoreHorizontalIcon } from 'vue-feather-icons';
import ContextualDropdown from '../../components/contextual-dropdown.vue';
import DocumentDeleteModal from './document-delete-modal.vue';

export default {
    components: {
        ContextualDropdown,
        DocumentDeleteModal,
        FileIcon,
        MoreHorizontalIcon,
    },

    props: {
        document: {},
        index: {},
        count: {},
        signatures_enabled: { default: false },
        has_templates: {},
        selection_mode: { default: false },
        selected_documents: { default: () => [] },
        disable_contextual_menus: { default: false },
        is_editable: { default: true },
    },

    data() {
        return {
            is_editing: false,
            show_delete_confirmation: false,
            display_filename: this.document.display_filename,
            new_filename: this.document.title || this.document.filename,
            show_dropdown: false,
            dropdownX: 0,
            dropdownY: 0,
            clickTimeout: null,
        }
    },

    computed: {
        selected_document_ids() {
            return this.selected_documents
                .filter(document => document.type === 'document')
                .map(document => document.id);
        },
        is_selected() {
            return this.selected_document_ids.includes(this.document.id);
        },
        dropdown_items() {
            const items = [];

            items.push({
                key: 'download',
                label: 'Download',
                icon: 'far fa-arrow-alt-circle-down',
                action: 'download',
                href: this.document.download_route,
            });

            if (this.document.is_viewable) {
                items.push({
                    key: 'view',
                    label: 'View',
                    icon: 'far fa-eye',
                    action: 'view',
                    href: this.document.show_route,
                    target: '_blank',
                });
            }

            if (this.document.is_viewable_via_google) {
                items.push({
                    key: 'view-inline',
                    label: 'View',
                    icon: 'far fa-eye',
                    action: 'view-inline',
                    href: this.document.show_inline_route,
                });
            }

            items.push({
                key: 'rename',
                label: 'Rename',
                icon: 'far fa-edit',
                action: 'rename',
            });

            items.push({
                key: 'delete',
                label: 'Delete',
                icon: 'far fa-trash-alt',
                action: 'delete',
                color: 'red',
            });

            return items;
        }
    },

    methods: {
        handleCheckboxClick(event) {
            this.toggleSelection();
        },
        handleRowClick(event) {
            if (this.is_editing) {
                return;
            }

            // If in selection mode, toggle the checkbox instead of opening dropdown
            if (this.selection_mode) {
                this.toggleSelection();
                return;
            }

            this.dropdownX = event.clientX;
            this.dropdownY = event.clientY;
            this.openDropdown();
        },
        closeDropdown() {
            this.show_dropdown = false;
        },
        openDropdown() {
            this.show_dropdown = true;
        },
        handleDropdownAction(item) {
            switch (item.action) {
                case 'rename':
                    this.editFilename();
                    break;
                case 'delete':
                    this.confirmDelete();
                    break;
                case 'view':
                case 'view-inline':
                case 'download':
                    if (item.href) {
                        if (item.target === '_blank') {
                            window.open(item.href, '_blank');
                        } else {
                            window.location.href = item.href;
                        }
                    }
                    break;
            }
        },
        editFilename() {
            this.is_editing = true;
            this.$nextTick(() => {
                this.$refs.input?.focus();
            });
        },
        cancelEditFilename() {
            this.is_editing = false;
            this.new_filename = this.document.title || this.document.filename;
        },
        saveFilename() {
            this.display_filename = this.new_filename;

            axios.patch(this.document.update_route, {
                    title: this.new_filename,
                })
                .then(response => {
                    this.is_editing = false;
                    // Emit the updated document data instead of just the ID
                    this.$emit('edited', response.data.data);
                })
                .catch(error => {
                    console.log(error);
                });
        },
        confirmDelete() {
            this.show_delete_confirmation = true;
        },
        confirmDeleteClosed() {
            this.show_delete_confirmation = false;
        },
        deletedFile() {
            this.$emit('deleted', this.document.id);
        },
        toggleSelection() {
            const new_selected_documents = [...this.selected_documents];
            const index = new_selected_documents.findIndex(d => d.type === 'document' && d.id === this.document.id);
            
            if (index > -1) {
                new_selected_documents.splice(index, 1);
            } else {
                new_selected_documents.push({
                    id: this.document.id,
                    folder_id: this.document.folder_id,
                    display_filename: this.document.display_filename,
                    order: null,
                    type: 'document',
                });
            }
            
            this.$emit('selection-changed', new_selected_documents);
        },
    },
}
</script>
